import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import useTenantFeatures from '@/hooks/useTenantFeatures';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import PricingPage from '@/pages/PricingPage';
import CheckoutPage from '@/pages/CheckoutPage';
import BillingSuccess from '@/pages/BillingSuccess';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import Dashboard from '@/pages/Dashboard';
import MysteryShoppingDashboard from '@/pages/MysteryShoppingDashboard';
import BranchManagement from '@/pages/BranchManagement';
import SupportedCountries from '@/pages/SupportedCountries';
import SubscriptionPage from '@/pages/SubscriptionPage';
import ValidationCodes from '@/pages/ValidationCodes';
import UserManagement from '@/pages/UserManagement';
import SurveyResponses from '@/pages/SurveyResponses';
import ResponseDetail from '@/pages/ResponseDetail';
import TaxonomyManagement from '@/pages/TaxonomyManagement';
import TaxonomyTemplates from '@/pages/TaxonomyTemplates';
import TaxonomyTemplateEditor from '@/pages/TaxonomyTemplateEditor';
import CaseManagement from '@/pages/CaseManagement';
import GapLibrary from '@/pages/GapLibrary';
import GapLibraryPrint from '@/pages/GapLibraryPrint';
import Settings from '@/pages/Settings';
import SurveyForm from '@/pages/SurveyForm';
import SurveyPreview from '@/pages/SurveyPreview';
import DemoQRCodes from '@/pages/DemoQRCodes';
import SurveysList from '@/pages/SurveysList';
import SurveyBuilder from '@/pages/SurveyBuilder';
import PlatformDashboard from '@/pages/PlatformDashboard';
import SetPasswordPage from '@/pages/SetPasswordPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import QAQueue from '@/pages/QAQueue';
import QAReview from '@/pages/QAReview';
import QAAdmin from '@/pages/QAAdmin';
import ReportsList from '@/pages/ReportsList';
import ExperiencePerformance from '@/pages/ExperiencePerformance';
import ComparePage from '@/pages/ComparePage';
import { COMPARE_ENABLED } from '@/lib/featureFlags';
import ReportView from '@/pages/ReportView';
import SatisfinderRevision from '@/pages/SatisfinderRevision';
import QualifierBulkEditor from '@/pages/QualifierBulkEditor';
import MyFindRevise from '@/pages/MyFindRevise';
import AdminPages from '@/pages/admin/AdminPages';
import { PublicPage, PublicArticle, PublicBlogIndex } from '@/components/cms/PublicRenderers';
import ProposalGate from '@/pages/ProposalGate';
import HomePage from '@/pages/HomePage';
import { HostEntryRoute } from '@/components/surveyAccess/HostEntryRoute';
import { SatisfinderAuthProvider } from '@/contexts/SatisfinderAuthContext';
import SatisfinderLanding from '@/pages/satisfinder/SatisfinderLanding';
import SatisfinderSignup from '@/pages/satisfinder/SatisfinderSignup';
import SatisfinderLogin from '@/pages/satisfinder/SatisfinderLogin';
import SatisfinderVerifyOTP from '@/pages/satisfinder/SatisfinderVerifyOTP';
import SatisfinderForgotPassword from '@/pages/satisfinder/SatisfinderForgotPassword';
import SatisfinderDashboard from '@/pages/satisfinder/SatisfinderDashboard';
import SatisfinderLegalPage from '@/pages/satisfinder/SatisfinderLegalPage';
import SatisfinderAdminList from '@/pages/satisfinder/SatisfinderAdminList';
import SatisfinderAdminProfile from '@/pages/satisfinder/SatisfinderAdminProfile';
import { VIEW_SATISFINDER_PROFILES, hasPermission } from '@/lib/staffPermissions';
import SatisfinderKYC from '@/pages/satisfinder/SatisfinderKYC';
// Feb 2026 — subscription-access gate + landing pages.
import { SubscriptionGate } from '@/hooks/useSubscriptionAccess';
import {
  CompleteSubscriptionPage,
  BillingIssuePage,
  SubscriptionArchivedPage,
} from '@/components/subscription/SubscriptionPages';
import SatisfinderPayout from '@/pages/satisfinder/SatisfinderPayout';
import SatisfinderAdminQueues from '@/pages/satisfinder/SatisfinderAdminQueues';

import { applyLegacyVoxRedirect } from '@/lib/legacyRedirect';

export const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// Oct 2026 — Redirect the two legacy VOX landing pages to the new satisFIND
// marketing site. Runs before React mounts (no content flash). Scoped to the
// production host vox.satisfind.com and exact paths only — see legacyRedirect.js.
applyLegacyVoxRedirect();

function PlatformAdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (!loading && user && user.role !== 'platform_admin') return <Navigate to="/dashboard" replace />;
  return <ProtectedRoute>{children}</ProtectedRoute>;
}


function AccessDenied() {
  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center" data-testid="access-denied">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#0F172A] font-['Montserrat'] mb-2">Access Denied</h2>
        <p className="text-[#64748B]">You don't have permission to access this page.</p>
        <a href="/dashboard" className="mt-4 inline-block btn-primary" data-testid="access-denied-home">Go to Dashboard</a>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, requiredRole, requiredPermission }) {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if ((requiredRole && user?.role !== requiredRole)
      || (requiredPermission && !hasPermission(user, requiredPermission))) return <AccessDenied />;
  // Feb 2026 — every authenticated route runs through the subscription gate.
  // Platform admins bypass it inside the gate itself.
  return <SubscriptionGate>{children}</SubscriptionGate>;
}

// Feb 2026 — Auth-required but NOT subscription-gated. Used only by the
// three subscription-state landing pages so blocked users can still reach
// them. Never wrap protected app content in this.
function AuthOnly({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  return children;
}

// Block the `client` role from internal tenant pages (Responses, Cases, Branches,
// Surveys, Users, Settings, Dashboard, etc). Clients must only ever see
// published Mystery Shopping FINDs via /reports — exposing the raw responses
// view would leak unpublished QA-in-progress data.
function TenantRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (user?.role === 'client') return <Navigate to="/reports" replace />;
  return <SubscriptionGate>{children}</SubscriptionGate>;
}

function BuilderSurveyPreview() {
  const { surveyId } = useParams();
  return <SurveyForm previewSurveyId={surveyId} />;
}

// Aug 2026 — Product-aware /dashboard dispatch. When the tenant has ONLY
// Mystery Shopping surveys (no NPS/CSAT/VOX), render the dedicated MS
// dashboard. Otherwise render the standard Dashboard which already gates
// its own MS summary card on has_mystery_shopping.
function DashboardRoute() {
  const features = useTenantFeatures();
  if (features._loaded && features.has_mystery_shopping && !features.has_standard_surveys) {
    return <MysteryShoppingDashboard />;
  }
  return <Dashboard />;
}

// Aug 2026 — Product-aware /responses guard. MS-only tenants must never
// see the standard responses list; a direct URL hit gets bounced to
// /reports. Waits for features to load so we don't misroute during the
// brief pre-fetch window (features hook returns _loaded:false by default).
function ResponsesRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const features = useTenantFeatures();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-[#64748B]">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (user?.role === 'client') return <Navigate to="/reports" replace />;
  if (features._loaded && features.has_mystery_shopping && !features.has_standard_surveys) {
    return <Navigate to="/reports" replace />;
  }
  return <SubscriptionGate>{children}</SubscriptionGate>;
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <SatisfinderAuthProvider>
      <Router>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HostEntryRoute fallback={<HomePage />} />} />
          <Route path="/go/:slug" element={<SurveyForm />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/survey/:branchId/:code" element={<SurveyForm />} />
          <Route path="/survey/:branchId/:code/preview" element={<SurveyPreview />} />
          <Route path="/survey/revise/:publicId" element={<SatisfinderRevision />} />
          <Route path="/demo/qr-codes" element={<DemoQRCodes />} />
          <Route path="/proposal/:slug" element={<ProposalGate />} />

          {/* satisFINDER® public + auth */}
          <Route path="/satisfinder" element={<SatisfinderLanding />} />
          <Route path="/satisfinder/signup" element={<SatisfinderSignup />} />
          <Route path="/satisfinder/login" element={<SatisfinderLogin />} />
          <Route path="/satisfinder/verify-email" element={<SatisfinderVerifyOTP />} />
          <Route path="/satisfinder/forgot-password" element={<SatisfinderForgotPassword />} />
          <Route path="/satisfinder/dashboard" element={<SatisfinderDashboard />} />
          <Route path="/satisfinder/kyc" element={<SatisfinderKYC />} />
          <Route path="/satisfinder/payout" element={<SatisfinderPayout />} />
          <Route path="/satisfinder/legal/:slug" element={<SatisfinderLegalPage />} />
          <Route path="/admin/satisfinders" element={<ProtectedRoute requiredPermission={VIEW_SATISFINDER_PROFILES}><SatisfinderAdminList /></ProtectedRoute>} />
          <Route path="/admin/satisfinders/:satisfinderId" element={<ProtectedRoute requiredPermission={VIEW_SATISFINDER_PROFILES}><SatisfinderAdminProfile /></ProtectedRoute>} />
          <Route path="/admin/supported-countries" element={<ProtectedRoute><SupportedCountries /></ProtectedRoute>} />
          <Route path="/admin/satisfinder-queues" element={<ProtectedRoute requiredRole="platform_admin"><SatisfinderAdminQueues /></ProtectedRoute>} />

          {/* Auth-protected */}
          {/* Feb 2026 — subscription landing pages. Auth-required but
              deliberately NOT wrapped in SubscriptionGate so blocked
              tenants can reach them. */}
          <Route path="/complete-subscription" element={<AuthOnly><CompleteSubscriptionPage /></AuthOnly>} />
          <Route path="/billing-issue"         element={<AuthOnly><BillingIssuePage />         </AuthOnly>} />
          <Route path="/subscription-archived" element={<AuthOnly><SubscriptionArchivedPage /> </AuthOnly>} />
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
          <Route path="/subscriptions/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
          <Route path="/dashboard" element={<TenantRoute><DashboardRoute /></TenantRoute>} />
          <Route path="/branches" element={<TenantRoute><BranchManagement /></TenantRoute>} />
          <Route path="/codes" element={<TenantRoute><ValidationCodes /></TenantRoute>} />
          <Route path="/users" element={<TenantRoute><UserManagement /></TenantRoute>} />
          <Route path="/admin/users" element={<TenantRoute><UserManagement /></TenantRoute>} />
          <Route path="/admin/subscription" element={<TenantRoute><SubscriptionPage /></TenantRoute>} />
          <Route path="/responses" element={<ResponsesRoute><SurveyResponses /></ResponsesRoute>} />
          <Route path="/responses/:responseId" element={<ResponsesRoute><ResponseDetail /></ResponsesRoute>} />
          <Route path="/taxonomy" element={<TenantRoute><TaxonomyManagement /></TenantRoute>} />
          <Route path="/platform/taxonomy-templates" element={<PlatformAdminOnly><TaxonomyTemplates /></PlatformAdminOnly>} />
          <Route path="/platform/taxonomy-templates/:templateId" element={<PlatformAdminOnly><TaxonomyTemplateEditor /></PlatformAdminOnly>} />
          <Route path="/cases" element={<TenantRoute><CaseManagement /></TenantRoute>} />
          <Route path="/gaps" element={<TenantRoute><GapLibrary /></TenantRoute>} />
          <Route path="/gaps/print" element={<TenantRoute><GapLibraryPrint /></TenantRoute>} />
          <Route path="/settings" element={<TenantRoute><Settings /></TenantRoute>} />
          <Route path="/surveys" element={<TenantRoute><SurveysList /></TenantRoute>} />
          <Route path="/survey-builder/:surveyId" element={<TenantRoute><SurveyBuilder /></TenantRoute>} />
          <Route path="/survey-builder/:surveyId/preview" element={<TenantRoute><BuilderSurveyPreview /></TenantRoute>} />
          <Route path="/surveys/:surveyId/qualifiers" element={<TenantRoute><QualifierBulkEditor /></TenantRoute>} />
          <Route path="/platform" element={<ProtectedRoute><PlatformDashboard /></ProtectedRoute>} />
          <Route path="/qa-queue" element={<ProtectedRoute><QAQueue /></ProtectedRoute>} />
          <Route path="/qa-review/:responseId" element={<ProtectedRoute><QAReview /></ProtectedRoute>} />
          <Route path="/qa-admin" element={<ProtectedRoute><QAAdmin /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ReportsList /></ProtectedRoute>} />
          <Route path="/experience-performance" element={<ProtectedRoute><ExperiencePerformance /></ProtectedRoute>} />
          <Route path="/compare" element={<ProtectedRoute>{COMPARE_ENABLED ? <ComparePage /> : <Navigate to="/reports" replace />}</ProtectedRoute>} />
          <Route path="/reports/:responseId" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
          <Route path="/my-finds/:responseId" element={<ProtectedRoute><MyFindRevise /></ProtectedRoute>} />

          {/* CMS admin */}
          <Route path="/admin/pages" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
          <Route path="/admin/pages/:id" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />

          {/* CMS public — blog index + article + root-slug page */}
          <Route path="/blog" element={<PublicBlogIndex />} />
          <Route path="/blog/:slug" element={<PublicArticle />} />
          <Route path="/404-cms" element={<PublicPage slugOverride="__404__" />} />

          {/* CMS root-slug catch-all — MUST be the last route before the SPA fallback. */}
          <Route path="/:slug" element={<PublicPage />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      <Toaster position="top-right" richColors offset={{ top: 76 }} mobileOffset={{ top: 76 }} />
      </SatisfinderAuthProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
